import { useEffect, useState } from 'react';
import {
  Flex,
  FlexColumn,
  FlexSpace,
  FormControls,
  InfoTooltip,
  Loading,
  Switch,
} from '@elementary/common-ui/components';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { INTEGRATION_OPTIONS } from '@elementary/common-ui/constants/queryKeys';
import { AlertDestinationItem } from 'shared/components/AlertDestinations';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import * as api from 'shared/services/api';
import useCustomAlertOverride from 'shared/hooks/useCustomAlertOverride';
import { RULE_ID } from '@elementary/common-ui/constants/urlParams';
import { AlertDestination } from '../../../types';
import AlertDestinationEmptyState from './AlertDestinationEmptyState';
import AlertOverrideSection from './AlertOverrideSection';
import DeleteAlertOverrideDialog from './DeleteAlertOverrideDialog';

type AccordionSummaryProps = {
  isMuted: boolean;
  onChange: (val: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
};

const AccordionSummary = ({
  isMuted,
  onChange,
  isLoading,
  disabled,
  disabledTooltip,
}: AccordionSummaryProps) => (
  <FlexSpace sx={{ width: '100%' }}>
    <Flex sx={{ alignItems: 'center', gap: 1 }}>
      <Typography>Notification Settings</Typography>
      <InfoTooltip
        title={
          <>
            Turn off to mute all notifications for this test, including rules,
            overrides, and any future matching rules.
            <br />
            Turn on to resume notifications for this test.
          </>
        }
      />
    </Flex>

    <Tooltip title={disabledTooltip}>
      <Flex
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <Switch
          checked={!isMuted}
          onChange={(_, val) => onChange(val)}
          disabled={isLoading || disabled}
        />
      </Flex>
    </Tooltip>
  </FlexSpace>
);

type Props = {
  isMuted: boolean;
  onChange: (val: boolean) => void;
  isLoading?: boolean;
  alertDestinations?: AlertDestination[];
  latestExecutionTestId?: string;
};

const TestNotificationAccordion = ({
  isMuted,
  onChange,
  isLoading,
  alertDestinations,
  latestExecutionTestId,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editDestination, setEditDestination] = useState<AlertDestination>();
  const [deleteDestination, setDeleteDestination] =
    useState<AlertDestination>();
  const [addNewCustomDestination, setAddNewCustomDestination] = useState(false);

  const hasPermission = useEnvPermission();
  const hasWritePermission = hasPermission('EDIT_ALERT_RULES');
  const selectedEnvId = useSelectedEnv();
  const navigate = useNavigateWithEnv();

  const { getDestinationItem } = useCustomAlertOverride();

  const {
    data: integrationOptionsData,
    isLoading: isIntegrationOptionsLoading,
  } = useQuery([INTEGRATION_OPTIONS, selectedEnvId], () =>
    api.fetchAlertIntegrations(),
  );

  const noIntegrations =
    !isIntegrationOptionsLoading && integrationOptionsData?.length === 0;

  const showAlertOverrideSection =
    hasWritePermission &&
    (addNewCustomDestination ||
      alertDestinations?.some((dest) =>
        ['CLOUD', 'CUSTOM'].includes(dest?.configurationType || ''),
      ));

  const showEmptyState = !alertDestinations?.length && !addNewCustomDestination;

  const currentCustomDestinations = alertDestinations?.filter(
    (dest) => dest?.configurationType === 'CUSTOM',
  );

  const handleExpand = (val: boolean) => {
    if (isMuted) return;
    setIsExpanded(val);
  };

  useEffect(() => {
    if (isMuted) {
      setIsExpanded(false);
    }
  }, [isMuted]);

  return (
    <FormControls.TestAccordion
      expanded={isExpanded}
      onChange={(_, val) => handleExpand(val)}
      summary={
        <AccordionSummary
          isMuted={isMuted}
          onChange={onChange}
          isLoading={isLoading || isIntegrationOptionsLoading}
          disabled={!hasWritePermission || noIntegrations}
          disabledTooltip={
            noIntegrations
              ? 'Connect your messaging app to start getting alerts from Elementary.'
              : !hasWritePermission
              ? 'No permissions to modify notification settings'
              : undefined
          }
        />
      }
    >
      <FlexColumn sx={{ gap: 1.5, p: 3 }}>
        {!!alertDestinations?.length && (
          <FormControls.FormTitle>
            Current Alert Destinations
          </FormControls.FormTitle>
        )}
        {isLoading ? (
          <Loading size={32} />
        ) : showEmptyState ? (
          <AlertDestinationEmptyState
            onAddCustomDestination={() => setAddNewCustomDestination(true)}
          />
        ) : (
          <FlexColumn sx={{ gap: 2 }}>
            {alertDestinations?.map((dest, index) => {
              const destinationItem = getDestinationItem(
                dest.type,
                dest.channel,
              );
              const channel = Array.isArray(destinationItem)
                ? destinationItem.map((item) => item.label).join(', ')
                : destinationItem?.label || '';

              return (
                <AlertDestinationItem
                  key={index}
                  type={dest.type}
                  channel={channel}
                  configurationType={dest.configurationType}
                  conditions={dest.conditions}
                  showEdit={dest.configurationType !== 'CODE'}
                  showDelete={dest.configurationType === 'CUSTOM'}
                  onEdit={() =>
                    dest.configurationType === 'CLOUD' && dest.ruleId
                      ? navigate(`/alert-rules?${RULE_ID}=${dest.ruleId}`)
                      : setEditDestination(dest)
                  }
                  onDelete={() => setDeleteDestination(dest)}
                />
              );
            })}
            {showAlertOverrideSection && (
              <AlertOverrideSection
                isOpen={addNewCustomDestination}
                onOpen={() => setAddNewCustomDestination(true)}
                onClose={() => {
                  setEditDestination(undefined);
                  setAddNewCustomDestination(false);
                }}
                editItem={editDestination}
                showAddDestinationButton={
                  editDestination?.configurationType !== 'CUSTOM'
                }
                currentOverrides={currentCustomDestinations}
                latestExecutionTestId={latestExecutionTestId}
              />
            )}
          </FlexColumn>
        )}
      </FlexColumn>

      <DeleteAlertOverrideDialog
        open={!!deleteDestination}
        onClose={() => setDeleteDestination(undefined)}
        alertDestination={deleteDestination}
        latestExecutionTestId={latestExecutionTestId}
      />
    </FormControls.TestAccordion>
  );
};

export default TestNotificationAccordion;
